import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import {
  IdentityAdminOverview,
  IdentityLayerAdminService,
} from '../../identity/services/identity-layer-admin.service';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { AdminToggleSwitchComponent } from '../../../shared/components/admin/admin-toggle-switch/admin-toggle-switch.component';

@Component({
  selector: 'app-identity-admin',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    RouterLink,
    MatProgressSpinnerModule,
    CtaButtonComponent,
    AdminPageShellComponent,
    AdminToggleSwitchComponent,
  ],
  templateUrl: './identity-admin.component.html',
  styleUrl: './identity-admin.component.scss',
})
export class IdentityAdminComponent implements OnInit {
  #identityAdmin = inject(IdentityLayerAdminService);

  overview = signal<IdentityAdminOverview | null>(null);
  overviewLoading = signal(true);
  overviewError = signal(false);

  layerToggleBusy = signal(false);
  courseRequirementToggleBusy = signal(false);
  backfillBusy = signal(false);
  backfillError = signal(false);
  backfillResult = signal<number | null>(null);

  ngOnInit(): void {
    void this.loadOverview();
  }

  async loadOverview(): Promise<void> {
    this.overviewLoading.set(true);
    this.overviewError.set(false);
    this.backfillResult.set(null);

    try {
      this.overview.set(await this.#identityAdmin.getOverview());
    } catch {
      this.overviewError.set(true);
    } finally {
      this.overviewLoading.set(false);
    }
  }

  async toggleLayer(enabled: boolean): Promise<void> {
    this.layerToggleBusy.set(true);
    this.backfillResult.set(null);

    try {
      await this.#identityAdmin.setLayerEnabled(enabled);
      this.overview.update((current) =>
        current ? { ...current, layerEnabled: enabled } : current,
      );
    } catch {
      this.overviewError.set(true);
    } finally {
      this.layerToggleBusy.set(false);
    }
  }

  async toggleFullyVerifiedRequiresCourse(enabled: boolean): Promise<void> {
    this.courseRequirementToggleBusy.set(true);
    this.backfillResult.set(null);

    try {
      await this.#identityAdmin.setFullyVerifiedRequiresCourseEnabled(enabled);
      this.overview.update((current) =>
        current
          ? { ...current, fullyVerifiedRequiresCourse: enabled }
          : current,
      );
    } catch {
      this.overviewError.set(true);
    } finally {
      this.courseRequirementToggleBusy.set(false);
    }
  }

  async runGraceBackfill(): Promise<void> {
    this.backfillBusy.set(true);
    this.backfillError.set(false);
    this.backfillResult.set(null);

    try {
      const result = await this.#identityAdmin.backfillLegacyGrace();
      this.backfillResult.set(result.assignedCount);
    } catch {
      this.backfillError.set(true);
    } finally {
      this.backfillBusy.set(false);
    }
  }
}
